<template>
  <div class="wrapper">
    <TopNoticeBanner ref="bannerRef" class="top-banner" />

    <template v-if="isAppReady">
      <div
        ref="headerRef"
        class="header-container"
        :class="{ 'is-fixed': isHeaderFixed }"
      >
        <headerComponent />
      </div>
      <div
        v-show="isHeaderFixed"
        :style="{ height: headerHeight + 'px' }"
      ></div>

      <SupportBanner />

      <div id="Contents" class="contents">
        <div class="contents-inner">
          <router-view />
          <div class="fab-container">
            <TopButton />
            <livechat />
          </div>
        </div>
      </div>

      <footerComponent />
    </template>

    <div v-else class="app-loading">
      <LoadingSpinner />
    </div>

    <ModalContainer />

    <SessionWarningModal
      :is-visible="isWarningVisible"
      :remaining-time="remainingTime"
      :total-warning-time="warningBefore"
      @extend="handleSessionExtend"
      @logout="handleSessionLogout"
    />
  </div>
</template>

<script setup>
/**
 * ==============================
 * 1) import 구문
 * ==============================
 */
import {
  ref,
  onMounted,
  onBeforeUnmount,
  getCurrentInstance,
  computed,
  watch,
  provide,
  nextTick,
} from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toast-notification";

// 🔁 Unhead
import { useHead } from "@unhead/vue";

import { SEO_CONFIG } from "@/constants/meta";

import { useMainStore } from "@/store/mainStore";

import headerComponent from "@/components/layouts/HeaderComponents.vue";
import footerComponent from "@/components/layouts/FooterComponents.vue";
import SupportBanner from "@/components/layouts/SupportBanner.vue";
import TopButton from "@/components/layouts/TopButton.vue";
import LoadingSpinner from "@/components/layouts/loading.vue";

import livechat from "@/components/common/LiveChat.vue";
import ModalContainer from "@/components/common/ModalContainer.vue";

import { logError, logWarn, logInfo, logDebug } from "@/utils/logHelper";

import { useAlertsStore } from "@/store/notifications/useAlertsStore";
import TopNoticeBanner from "@/components/layouts/TopNoticeBanner.vue";

// ✅ 자동 로그아웃 관련 import
import SessionWarningModal from "@/components/common/SessionWarningModal.vue";
import { useAutoLogout } from "@/composables/useAutoLogout";
import { useCustomerSse } from "@/composables/useCustomerSse";

/**
 * ==============================
 * 2) 상수 / 스토어 / 인스턴스
 * ==============================
 */
// i18n
const { t, locale: i18nLocale } = useI18n({ useScope: "global" });

const AFTER_LOGIN_NEXT_KEY = "mpnite_after_login_next";
const SKIP_WELCOME_KEY = "mpnite_skip_welcome_once";
const PENDING_OPEN_LOGIN_KEY = "mpnite_pending_open_login";

// 라우트 & 라우터
const route = useRoute();
const router = useRouter();

// 토스트
const toast = useToast();

// 스토어
const mainStore = useMainStore();
const alertsStore = useAlertsStore();

const { proxy } = getCurrentInstance();

const { connect: connectSse, disconnect: disconnectSse } = useCustomerSse(
  (data) => {
    console.log("🔥🔥🔥 [알림 도착!!!] 데이터:", data);
    alertsStore.refreshNow();
    alertsStore.pushEstimateAlert({
      title: "견적이 수정되었습니다",
      body: `"${data?.estimateTitle || ""}" 견적을 확인해주세요.`,
      adminEstimateId: data?.adminEstimateId,
    });

    // 토스트 직전에 로그 추가
    console.log("토스트 호출 직전, toast 객체:", toast);
    toast.info(
      `견적이 수정되었습니다: "${data.estimateTitle || ""}"\n장바구니에서 확인해주세요.`,
      { duration: 8000 },
    );
    console.log("토스트 호출 완료");
  },
);

/** ✅ 브랜드 / 사이트 상수 */
const BRAND_NAME_KO = "엠피니티";
const BRAND_NAME_EN = "MPNITE";
const SITE_NAME = "엠피니티 (MPNITE)";

const DEFAULT_TITLE = SEO_CONFIG.naver.title;
const DEFAULT_DESCRIPTION = SEO_CONFIG.naver.description;

const SITE_URL = (process.env.VUE_APP_SITE_URL || "https://mpnite.com").replace(
  /\/+$/,
  "",
);
const SITE_HOST = new URL(SITE_URL).host;

const SEO_KEYWORDS = [
  "3D프린팅 플랫폼",
  "온라인 제조플랫폼",
  "AI 자동견적",
  "시제품제작",
  "CNC가공",
  "사출성형",
  "엠피니티",
  "MPNITE",
].join(", ");

/** i18n locale → og:locale 매핑 */
const LOCALE_TO_OG_LOCALE = Object.freeze({
  ko: "ko_KR",
  en: "en_US",
  ja: "ja_JP",
});

/** i18n locale → inLanguage 매핑 */
const LOCALE_TO_IN_LANGUAGE = Object.freeze({
  ko: "ko-KR",
  en: "en-US",
  ja: "ja-JP",
});

/**
 * ==============================
 * 3) 로케일 설정 상수
 * ==============================
 */
const DEFAULT_COUNTRY = "KR";
const DEFAULT_LANG = "ko";
const DEFAULT_CURRENCY = "KRW";

const COUNTRY_TO_LANG_MAP = Object.freeze({ KR: "ko", US: "en", JP: "ja" });
const COUNTRY_TO_CURRENCY_MAP = Object.freeze({
  KR: "KRW",
  US: "USD",
  JP: "JPY",
});
const LANG_TO_LOCALE_MAP = Object.freeze({
  ko: "ko-KR",
  en: "en-US",
  ja: "ja-JP",
});

/**
 * ==============================
 * 4) 자동 로그아웃 설정
 * ==============================
 */
const IDLE_TIMEOUT = Number(process.env.VUE_APP_IDLE_TIMEOUT) || 30 * 60 * 1000;
const WARNING_BEFORE =
  Number(process.env.VUE_APP_IDLE_WARNING) || 5 * 60 * 1000;

const {
  isWarningVisible,
  remainingTime,
  remainingFormatted,
  extendSession,
  logoutNow,
  pause: pauseAutoLogout,
  resume: resumeAutoLogout,
  warningBefore,
  getStatus,
} = useAutoLogout({
  timeout: IDLE_TIMEOUT,
  warningBefore: WARNING_BEFORE,
  syncTabs: true,
  onWarning: () => {
    logInfo("[App] 세션 만료 경고");
  },
  onLogout: () => {
    logWarn("[App] 세션 만료로 자동 로그아웃");
    toast.warning("세션이 만료되어 자동 로그아웃되었습니다.", {
      duration: 5000,
    });
  },
});

// 세션 타이머 정보 provide
provide("sessionTimer", {
  remainingTime,
  remainingFormatted,
  isWarningVisible,
  extendSession,
  isActive: computed(() => mainStore.isLogin),
});

/**
 * ==============================
 * 5) 세션 이벤트 핸들러
 * ==============================
 */
function handleSessionExtend() {
  extendSession();
  toast.success("세션이 연장되었습니다.", { duration: 3000 });
  logInfo("[App] 사용자가 세션 연장");
}

async function handleSessionLogout() {
  await logoutNow();
  toast.info("로그아웃되었습니다.");
}

/**
 * ==============================
 * 6) SEO용 computed (✅ 개선)
 * ==============================
 */

// ✅ Canonical URL - query string 제거, trailing slash 정규화
const canonicalUrl = computed(() => {
  const path = route.path;
  const normalizedPath = path === "/" ? "" : path.replace(/\/$/, "");
  return `${SITE_URL}${normalizedPath}`;
});

// ✅ 페이지 제목 (route meta에서)
const pageTitle = computed(() => route.meta?.title || null);

// ✅ 전체 제목 (titleTemplate 역할)
const fullTitle = computed(() => {
  if (!pageTitle.value) return DEFAULT_TITLE;
  // 이미 브랜드 포함 시 그대로 반환
  if (
    pageTitle.value.includes(SITE_NAME) ||
    pageTitle.value.includes(BRAND_NAME_KO)
  ) {
    return pageTitle.value;
  }
  return `${pageTitle.value} | ${SITE_NAME}`;
});

// ✅ 페이지 설명
const pageDescription = computed(() => {
  return route.meta?.description || DEFAULT_DESCRIPTION;
});

// ✅ og:locale
const ogLocale = computed(() => {
  return LOCALE_TO_OG_LOCALE[i18nLocale.value] || "ko_KR";
});

// ✅ inLanguage (Schema.org용)
const inLanguage = computed(() => {
  return LOCALE_TO_IN_LANGUAGE[i18nLocale.value] || "ko-KR";
});

/**
 * ==============================
 * 7) Unhead - <head> / SEO 메타 관리 (✅ key 속성 추가)
 * ==============================
 */
useHead(() => {
  const noIndex = route.meta?.noIndex === true;

  return {
    // ✅ title: 완성된 제목 직접 설정
    title: fullTitle.value,

    meta: [
      // ✅ 기본 SEO (key로 중복 방지)
      {
        key: "description",
        name: "description",
        content: pageDescription.value,
      },
      {
        key: "keywords",
        name: "keywords",
        content: SEO_KEYWORDS,
      },

      // ✅ Robots (noIndex 라우트용)
      ...(noIndex
        ? [{ key: "robots", name: "robots", content: "noindex,nofollow" }]
        : []),

      // ✅ Open Graph (key로 중복 방지)
      {
        key: "og:site_name",
        property: "og:site_name",
        content: `${SITE_NAME}`,
      },
      {
        key: "og:type",
        property: "og:type",
        content: "website",
      },
      {
        key: "og:title",
        property: "og:title",
        content: fullTitle.value,
      },
      {
        key: "og:description",
        property: "og:description",
        content: pageDescription.value,
      },
      {
        key: "og:image",
        property: "og:image",
        content: `${SITE_URL}/og-image.png`,
      },
      {
        key: "og:image:alt",
        property: "og:image:alt",
        content: "쉽고 빠른 3D프린팅 출력·제작 플랫폼 | 엠피니티 (MPNITE)", // ✅ 닫는 따옴표 수정
      },
      {
        key: "og:image:width",
        property: "og:image:width",
        content: "1200",
      },
      {
        key: "og:image:height",
        property: "og:image:height",
        content: "630",
      },
      {
        key: "og:url",
        property: "og:url",
        content: canonicalUrl.value,
      },
      {
        key: "og:locale",
        property: "og:locale",
        content: ogLocale.value,
      },

      // ✅ Twitter Card (key로 중복 방지)
      {
        key: "twitter:card",
        name: "twitter:card",
        content: "summary_large_image",
      },
      {
        key: "twitter:site",
        name: "twitter:site",
        content: "@MPNITE_COM",
      },
      {
        key: "twitter:title",
        name: "twitter:title",
        content: fullTitle.value,
      },
      {
        key: "twitter:description",
        name: "twitter:description",
        content: pageDescription.value,
      },
      {
        key: "twitter:image",
        name: "twitter:image",
        content: `${SITE_URL}/og-image.png`,
      },
      {
        key: "twitter:domain",
        name: "twitter:domain",
        content: SITE_HOST,
      },
    ],

    // HTML lang 속성
    htmlAttrs: {
      lang: i18nLocale.value || "ko",
    },

    // Canonical URL (key로 중복 방지)
    link: [
      {
        key: "canonical",
        rel: "canonical",
        href: canonicalUrl.value,
      },
    ],
  };
});

/**
 * ==============================
 * 9) Reactivity 변수
 * ==============================
 */
const isAppReady = ref(false);

/**
 * ==============================
 * 10) 헬퍼 함수
 * ==============================
 */
function normalizeNextPath(v) {
  if (typeof v !== "string") return "";
  if (!v.startsWith("/")) return "";
  return v;
}

async function flushPendingOpenLogin() {
  if (sessionStorage.getItem(PENDING_OPEN_LOGIN_KEY) !== "1") return;

  if (mainStore.isLogin) {
    sessionStorage.removeItem(PENDING_OPEN_LOGIN_KEY);
    return;
  }

  sessionStorage.removeItem(PENDING_OPEN_LOGIN_KEY);

  await nextTick();

  const cur = router.currentRoute.value;
  const fallback = "/";
  const nextPath =
    cur.path === "/login" ? cur.query.next || fallback : cur.fullPath;

  router.push({ path: "/login", query: { next: nextPath } });
  logDebug("[App] 로그인 라우트로 이동 완료");
}

/**
 * ✅ URL 쿼리 파라미터로 언어 설정 (QR 코드 대응)
 * - ?lang=en 같은 파라미터가 있으면 해당 언어로 전환
 * - localStorage에도 저장해서 다음 방문 때도 유지
 * - 반환값: 적용된 언어 코드 또는 null
 */
function setupLocaleFromQuery() {
  // 배열로 들어올 가능성 대비 (예: ?lang=en&lang=ko)
  const rawLang = route.query.lang;
  const langFromQuery = Array.isArray(rawLang) ? rawLang[0] : rawLang;

  // 파라미터가 없거나 지원하지 않는 언어면 무시
  if (!langFromQuery) return null;

  // 지원하는 언어인지 확인 (ko, en, ja)
  const supportedLangs = Object.values(COUNTRY_TO_LANG_MAP);
  if (!supportedLangs.includes(langFromQuery)) {
    logWarn("[App] 지원하지 않는 언어 파라미터:", langFromQuery);
    return null;
  }

  // 언어에 맞는 국가/통화 찾기
  const countryCode =
    Object.keys(COUNTRY_TO_LANG_MAP).find(
      (key) => COUNTRY_TO_LANG_MAP[key] === langFromQuery,
    ) || DEFAULT_COUNTRY;
  const currencyCode = COUNTRY_TO_CURRENCY_MAP[countryCode] || DEFAULT_CURRENCY;
  const finalLocale = LANG_TO_LOCALE_MAP[langFromQuery] || "ko-KR";

  // 스토어에 반영
  mainStore.SET_COUNTRY(countryCode);
  mainStore.SET_CURRENCY(currencyCode);
  mainStore.SET_LANGUAGE(langFromQuery);
  mainStore.SET_LOCALE(finalLocale);

  // localStorage에 저장 (다음 방문 시 유지)
  localStorage.setItem("countryCode", countryCode);
  localStorage.setItem("languageCode", langFromQuery);
  localStorage.setItem("currencyCode", currencyCode);

  // i18n에 적용
  i18nLocale.value = langFromQuery;

  logInfo("[App] URL 쿼리로 언어 설정:", {
    countryCode,
    languageCode: langFromQuery,
    currencyCode,
  });

  return langFromQuery;
}

/**
 * ✅ 빠른 로케일 설정 (동기, localStorage만 확인)
 */
function setupLocaleAndCountryFast() {
  const countryCode = localStorage.getItem("countryCode") || DEFAULT_COUNTRY;
  const currencyCode = localStorage.getItem("currencyCode") || DEFAULT_CURRENCY;
  const languageCode =
    localStorage.getItem("languageCode") ||
    COUNTRY_TO_LANG_MAP[countryCode] ||
    DEFAULT_LANG;
  const finalLocale = LANG_TO_LOCALE_MAP[languageCode] || "ko-KR";

  mainStore.SET_COUNTRY(countryCode);
  mainStore.SET_CURRENCY(currencyCode);
  mainStore.SET_LANGUAGE(languageCode);
  mainStore.SET_LOCALE(finalLocale);

  i18nLocale.value = languageCode;

  logDebug("[App] Fast locale setup:", {
    countryCode,
    languageCode,
    currencyCode,
  });
}

/**
 * ✅ 백그라운드 IP 조회 (화면 블로킹 없음)
 */
async function fetchIpLocationInBackground() {
  if (localStorage.getItem("countryCode")) {
    logDebug("[App] Skip IP lookup - localStorage already set");
    return;
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    const response = await fetch("https://ipwho.is/", {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();

    if (data.success && data.country_code) {
      const countryCode = data.country_code;

      if (!COUNTRY_TO_LANG_MAP[countryCode]) {
        logDebug("[App] Unsupported country from IP:", countryCode);
        return;
      }

      const languageCode = COUNTRY_TO_LANG_MAP[countryCode];
      const currencyCode = COUNTRY_TO_CURRENCY_MAP[countryCode];
      const finalLocale = LANG_TO_LOCALE_MAP[languageCode];

      mainStore.SET_COUNTRY(countryCode);
      mainStore.SET_CURRENCY(currencyCode);
      mainStore.SET_LANGUAGE(languageCode);
      mainStore.SET_LOCALE(finalLocale);

      localStorage.setItem("countryCode", countryCode);
      localStorage.setItem("languageCode", languageCode);
      localStorage.setItem("currencyCode", currencyCode);

      i18nLocale.value = languageCode;

      logInfo("[App] Locale updated from IP (background):", {
        countryCode,
        languageCode,
        currencyCode,
      });
    }
  } catch (error) {
    if (error.name === "AbortError") {
      logWarn("[App] IP lookup timeout (3s)");
    } else {
      logWarn("[App] IP lookup failed:", error?.message);
    }
  }
}

/**
 * 세션 만료 메시지 처리
 */
function handleSessionExpiredMessage() {
  const reason = route.query.reason;

  if (reason === "session_expired") {
    toast.warning("세션이 만료되어 로그아웃되었습니다. 다시 로그인해주세요.", {
      duration: 6000,
    });
    router.replace({
      ...route,
      query: { ...route.query, reason: undefined },
    });
  } else if (reason === "logged_out_other_tab") {
    toast.info("다른 탭에서 로그아웃되었습니다.", {
      duration: 5000,
    });
    router.replace({
      ...route,
      query: { ...route.query, reason: undefined },
    });
  }
}

function handleOpenLoginFromQuery() {
  const openLogin = route.query.openLogin;
  const nextRaw = route.query.next;

  if (openLogin !== "1") return;

  const next = normalizeNextPath(nextRaw);

  sessionStorage.setItem(SKIP_WELCOME_KEY, "1");

  const { openLogin: _ol, next: _nx, ...rest } = route.query;
  router.replace({ path: route.path, query: rest }).catch(() => {});

  if (mainStore.isLogin) {
    if (next) router.push(next).catch(() => {});
    return;
  }

  if (next) sessionStorage.setItem(AFTER_LOGIN_NEXT_KEY, next);
  sessionStorage.setItem(PENDING_OPEN_LOGIN_KEY, "1");

  if (isAppReady.value) {
    flushPendingOpenLogin();
  }
}

/**
 * ==============================
 * 11) Watchers
 * ==============================
 */

// watch - SSE 부분 수정
watch(
  () => mainStore.isLogin,
  (loggedIn, prevLoggedIn) => {
    // ✅ SSE는 onMounted에서 처리하므로 여기선 로그아웃 시 해제만
    if (!loggedIn) {
      disconnectSse();
    }

    if (loggedIn && !prevLoggedIn) {
      alertsStore.onLogin();
      // ✅ 로그인 직후(페이지 전환 없이 로그인한 경우)에만 SSE 연결
      connectSse();
      const next = sessionStorage.getItem(AFTER_LOGIN_NEXT_KEY);
      if (next) {
        sessionStorage.removeItem(AFTER_LOGIN_NEXT_KEY);
        router.push(next).catch(() => {});
      }
      resumeAutoLogout();
      logDebug("[App] 로그인 감지 → 자동 로그아웃 활성화");
    } else if (!loggedIn && prevLoggedIn) {
      alertsStore.onLogout();
      pauseAutoLogout();
      logDebug("[App] 로그아웃 감지 → 자동 로그아웃 비활성화");
    }
  },
  { immediate: false }, // ✅ immediate: false 로 변경
);

watch(
  () => route.fullPath,
  () => {
    if (mainStore.isLogin) extendSession();
    handleOpenLoginFromQuery();
  },
  { immediate: true },
);
watch(isAppReady, async (ready) => {
  if (ready) {
    await nextTick(); // DOM이 다 그려질 때까지 대기
    handleScroll(); // 높이 재계산

    // ✅ 가림막(로딩 스피너) 제거 로직 추가
    const loader = document.getElementById("global-route-loader");
    if (loader && loader.style.display !== "none") {
      loader.style.opacity = "0"; // 부드럽게 페이드아웃
      setTimeout(() => {
        loader.remove(); // DOM에서 완전히 삭제
      }, 300);
    }
  }
});

// ==========================================
//  추가할 스크롤 고정 (Fixed) 로직
// ==========================================
const isHeaderFixed = ref(false);
const headerRef = ref(null);
const bannerRef = ref(null);
const headerHeight = ref(0);

function handleScroll() {
  // 배너가 화면에 렌더링되어 있는지 안전하게 확인
  const bannerEl = bannerRef.value?.$el;
  // Node.ELEMENT_NODE(1)인지 확인하여 주석 노드 등 예외 상황 방지
  const bannerHeight =
    bannerEl && bannerEl.nodeType === 1 ? bannerEl.offsetHeight : 0;

  if (window.scrollY > bannerHeight && window.scrollY > 0) {
    // iOS 바운스 방지
    if (!isHeaderFixed.value) {
      // 헤더 엘리먼트가 존재할 때만 높이 계산
      if (headerRef.value) {
        headerHeight.value = headerRef.value.offsetHeight || 0;
      }
      isHeaderFixed.value = true;
    }
  } else {
    isHeaderFixed.value = false;
  }
}
/**
 * ==============================
 * 12) 라이프사이클 훅
 * ==============================
 */
onMounted(async () => {
  alertsStore.startPolling();

  try {
    await router.isReady();

    // ✅ URL 쿼리 파라미터(?lang=en) 우선 처리 - QR 코드 대응
    const langFromQuery = setupLocaleFromQuery();

    // URL에 언어 파라미터가 없을 때만 localStorage/기본값으로 설정
    if (!langFromQuery) {
      setupLocaleAndCountryFast();
    }

    isAppReady.value = true;
    await nextTick();

    await mainStore.checkAuthAction(); // 로그인 확인

    // ✅ checkAuthAction 완료 후 isLogin이 true면 SSE 연결
    if (mainStore.isLogin) {
      connectSse();
    }

    flushPendingOpenLogin();

    mainStore.MARK_LOCALE_READY();

    handleSessionExpiredMessage();

    fetchIpLocationInBackground();

    logInfo("[App] Initialization complete:", {
      country: mainStore.country,
      lang: mainStore.language,
      locale: mainStore.locale,
      currency: mainStore.currency,
      path: route.path,
    });

    if (process.env.NODE_ENV === "development") {
      window.__getSessionStatus = getStatus;
      logDebug("[App] 세션 상태 확인: window.__getSessionStatus()");
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
  } catch (error) {
    logError("[App] Initialization error:", error);
    isAppReady.value = true;
    proxy?.$toast?.error("초기화 중 오류가 발생했습니다.");
  }
});

onBeforeUnmount(() => {
  alertsStore.stopPolling();
  disconnectSse(); // 추가
  window.removeEventListener("scroll", handleScroll);
});
</script>

<style scoped>
.header-container {
  width: 100%;
  background-color: #ffffff;
  position: relative;
  z-index: 1020;
}

.header-container.is-fixed {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1030;
  /* ✅ fixed일 때만 한 단계 위로 */
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  animation: slideDown 0.2s ease-in-out;
}

.top-banner {
  position: relative;
  z-index: 1031;
  /* ✅ fixed 헤더보다 항상 위 */
}

/* 헤더가 고정될 때 부드럽게 내려오는 효과 */
@keyframes slideDown {
  from {
    transform: translateY(-100%);
  }

  to {
    transform: translateY(0);
  }
}

/* ✅ 전체 래퍼 (중요: overflow 설정을 풀어줘야 sticky가 작동함) */
.wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  overflow: visible !important;
}

/* --- 이하 기존 스타일 유지 --- */
.contents {
  flex-grow: 1;
}

.fab-container {
  position: fixed;
  right: 5rem;
  bottom: 2.5rem;
  z-index: 1050;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.app-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #fff;
}
</style>
